import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GoodsFilter, GoodsResponse } from '../../modules/Goods/types';
import { GoodsReqAPI } from '../../modules/Goods/api';
import { useCustomToast } from '../../components';
import { DownloadFileReqAPI } from '../../share/api/downloadFileService';

// Query key for caching
const keyQuery = ['goods'] as const;

export const useFetchGoodsRequests = (filters?: Partial<GoodsFilter>) => {
  return useQuery<GoodsResponse, Error>({
    queryKey: [keyQuery, filters || {}],
    queryFn: ({ queryKey }) => GoodsReqAPI.gets({ queryKey }),
    enabled: true,
  });
};

// Fetch a single goods request by ID
export const useFetchGoodsById = (id?: string) => {
  return useQuery({
    queryKey: [...keyQuery, id],
    queryFn: () => (id ? GoodsReqAPI.get(id) : Promise.resolve(undefined)),
    enabled: !!id,
  });
};

// Create a new goods request
export const useCreateGoods = () => {
  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: GoodsReqAPI.post,
    onSuccess: () => {
      showToast('success', 'Se ha creado la solicitud');
      queryClient.invalidateQueries({ queryKey: [keyQuery, {}] });
    },
  });
};

// Delete a goods request
export const useDeleteGoods = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => GoodsReqAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keyQuery });
    },
  });
};

//Export excel
export const useExportGoods = () => {
  const { showToast } = useCustomToast();
  return useMutation({
    mutationFn: (ids: string[]) => GoodsReqAPI.export(ids),
    onSuccess: () => {
      showToast('success', 'Exportación exitosa');
    },
    onError: () => {
      showToast('error', 'Error al exportar');
    },
  });
};

// Download files
export const useDownloadFilesGoods = () => {
  const { showToast } = useCustomToast();
  return useMutation({
    mutationFn: (id: string) => GoodsReqAPI.downloadFiles(id),
    onSuccess: () => {
      showToast('success', 'Exportación exitosa');
    },
    onError: () => {
      showToast('error', 'Error al exportar');
    },
  });
};

// Download Pdf
export const useDownloadPDFGoods = () => {
  const { showToast } = useCustomToast();
  return useMutation({
    mutationFn: (id: string) => DownloadFileReqAPI.downloadPDF(id),
    onSuccess: () => {
      showToast('success', 'Certificado exitoso');
    },
    onError: () => {
      showToast('error', 'Error al exportar');
    },
  });
};

// Cancel a request
export const useCancelGoods = () => {
  const { showToast } = useCustomToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => GoodsReqAPI.annulled(id, data),
    onSuccess: () => {
      showToast('success', 'Solicitud anulada');
    },
    onError: () => {
      showToast('error', 'Error al anular solicitud');
    },
  });
};